import React, { ReactElement } from 'react';
import { useGetSubFundSummary } from '../../../../api/sub-fund-setup/hooks/queries';
import { SubFundSummaryData } from '../../../../api/sub-fund-setup/types';
import GridItem from '../../../layout/GridComponents/GridItem';
import CustomTable from '../../../tables/CustomTable';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import {
  SubFundSummaryBasicInfoData,
  SubFundSummaryCounterpartyTable,
  SubFundSummaryRiskComplianceRulesTable,
  SubFundSummaryStressTestTable,
  generateBasicInfo,
  generateCounterpartyInfo,
  generateData,
  generateRiskComplianceRulesInfo,
  generateStressTestInfo,
  getSubFundSummaryBasicInfoColumns,
  stressTestColumns,
  subFundSummaryCounterpartyColumns,
  subFundSummaryRiskComplianceColumns,
} from './SubFundSummary.data';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';

const STRESS_TEST_SECTIONS = [
  { title: 'Risk Sector Stress Tests', key: 'risk_sector' as const },
  { title: 'Relative Stress Tests', key: 'relative' as const },
  { title: 'Historical Stress Tests', key: 'historical' as const },
];

const toolbarTitleStyle = {
  fontSize: '1.12rem',
  fontWeight: 500,
  transform: 'translate(-0.31rem, -0.31rem)',
};

function SubFundSummaryPage({
  fundId,
  fundName,
}: FundInfoComponentProps): ReactElement {
  const subFundSummaryQuery = useGetSubFundSummary(fundId);

  return (
    <QueryBoundary<SubFundSummaryData>
      isLoading={subFundSummaryQuery.isLoading}
      isFetching={subFundSummaryQuery.isFetching}
      isError={subFundSummaryQuery.isError}
      error={subFundSummaryQuery.error}
      data={subFundSummaryQuery.data}
      loadingMessages={['Loading Sub Fund Summary...']}
      noDataMessage="No sub fund summary data available."
      errorMessage="Unable to load sub fund summary data."
    >
      {(summaryData) => (
        <>
          <GridItem xs={12} card>
            <CustomTable<SubFundSummaryBasicInfoData>
              data={generateData(summaryData, generateBasicInfo)}
              columns={getSubFundSummaryBasicInfoColumns(
                summaryData.var_approach === 'relative',
              )}
              options={{
                paging: false,
              }}
              toolbarComponents={{
                titleStyle: toolbarTitleStyle,
              }}
              showToolbar
              title={fundName}
            />
          </GridItem>
          {summaryData.risk_compliance ? (
            <GridItem xs={3} card>
              <CustomTable<SubFundSummaryRiskComplianceRulesTable>
                data={generateData(summaryData, generateRiskComplianceRulesInfo)}
                columns={subFundSummaryRiskComplianceColumns}
                options={{
                  paging: false,
                }}
                multipleTablePdfParameters={{
                  specifiedWidth: 3,
                  tablesInRow: 5,
                }}
                toolbarComponents={{
                  titleStyle: toolbarTitleStyle,
                }}
                showToolbar
                title="Prospectus Restrictions Rules"
              />
            </GridItem>
          ) : null}
          {STRESS_TEST_SECTIONS.map(({ title, key }) => (
            <GridItem xs={2} card key={title}>
              <CustomTable<SubFundSummaryStressTestTable>
                multipleTablePdfParameters={{
                  specifiedWidth: 2,
                  tablesInRow: 5,
                }}
                options={{
                  paging: false,
                }}
                columns={stressTestColumns}
                data={generateData(summaryData, generateStressTestInfo, key)}
                title={title}
                toolbarComponents={{
                  titleStyle: toolbarTitleStyle,
                }}
                showToolbar
              />
            </GridItem>
          ))}
          {summaryData.counterparty_data ? (
            <GridItem xs={3} card>
              <CustomTable<SubFundSummaryCounterpartyTable>
                options={{
                  paging: false,
                }}
                multipleTablePdfParameters={{
                  specifiedWidth: 3,
                  tablesInRow: 5,
                }}
                columns={subFundSummaryCounterpartyColumns}
                data={generateData(summaryData, generateCounterpartyInfo)}
                title="Counterparty Data"
                showToolbar
                toolbarComponents={{
                  titleStyle: toolbarTitleStyle,
                }}
              />
            </GridItem>
          ) : null}
        </>
      )}
    </QueryBoundary>
  );
}

export default SubFundSummaryPage;
